/**
 * Auth events, and the referral-signup payload.
 *
 * Auth events (`user_login`, `user_signed_up`) are emitted in two steps:
 *   1. When Cognito sign-in succeeds, the call site marks a pending event
 *      via `setPendingAuthEvent`. We do NOT push to the dataLayer yet
 *      because the user's `sandId` isn't known at that point — it lives
 *      on the `viewer` GraphQL query and requires an authenticated round
 *      trip to the gateway.
 *   2. Once the viewer query resolves, `AnalyticsTracker` calls
 *      `consumePendingAuthEvent(sandId)` which emits the dataLayer event
 *      enriched with the sandId and clears the flag.
 *
 * The pending flag lives in `sessionStorage` so it survives full page
 * reloads (critical for social/OAuth flows where the browser navigates
 * off-site and comes back).
 */

import {
  AnalyticsEvent,
  LoginMethod,
  UtmParams,
  trackEvent,
} from './dataLayer';

export enum AuthEventType {
  Login = 'login',
  Signup = 'signup',
}

const PENDING_AUTH_EVENT_KEY = 'ehub:pendingAuthEvent';

interface PendingAuthEvent {
  type: AuthEventType;
  method: LoginMethod;
}

/**
 * Record that a login or signup just succeeded. Called from auth screen
 * callbacks (SignIn, ConfirmSignUp) and from the CognitoProvider social
 * sign-in handler. The matching dataLayer event is fired later by
 * `consumePendingAuthEvent` once the viewer query resolves and the
 * sandId is known.
 */
export const setPendingAuthEvent = (
  type: AuthEventType,
  method: LoginMethod
) => {
  if (typeof window === 'undefined') return;
  // A signup confirmed through the emailed link usually lands on the login
  // page next; that login must not replace the signup still waiting for its
  // sandId.
  if (
    type === AuthEventType.Login &&
    sessionStorage.getItem(PENDING_AUTH_EVENT_KEY)
  )
    return;
  const payload: PendingAuthEvent = { type, method };
  sessionStorage.setItem(PENDING_AUTH_EVENT_KEY, JSON.stringify(payload));
};

/**
 * If a pending auth event exists in sessionStorage, fire the matching
 * dataLayer event with the sandId and clear the flag. Returns `true` if
 * an event was fired, `false` otherwise. Safe to call repeatedly — only
 * the first call with a pending flag will produce an event.
 */
export const consumePendingAuthEvent = (sandId: string): boolean => {
  if (typeof window === 'undefined') return false;
  const raw = sessionStorage.getItem(PENDING_AUTH_EVENT_KEY);
  if (!raw) return false;

  sessionStorage.removeItem(PENDING_AUTH_EVENT_KEY);

  let pending: PendingAuthEvent;
  try {
    pending = JSON.parse(raw) as PendingAuthEvent;
  } catch {
    return false;
  }

  trackEvent(
    pending.type === AuthEventType.Login
      ? AnalyticsEvent.UserLogin
      : AnalyticsEvent.UserSignedUp,
    {
      method: pending.method,
      user_id: sandId,
    }
  );
  return true;
};

const SOCIAL_METHODS: readonly LoginMethod[] = [
  LoginMethod.Google,
  LoginMethod.Facebook,
  LoginMethod.LinkedIn,
  LoginMethod.Ehub,
];

/**
 * `CognitoProvider`'s `onSocialSignIn` handler. Amplify cannot distinguish a
 * first-time social signup from a returning social login at the Hub layer, so
 * social flows are always recorded as `user_login`. `user_signed_up` for social
 * would need a backend "is new account" signal.
 */
export const handleSocialSignIn = (provider: string) => {
  const method = SOCIAL_METHODS.find((m) => m === provider);
  if (method) setPendingAuthEvent(AuthEventType.Login, method);
};

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

/** The referral events' payload: the UTM parameters on the current url. */
export const utmParams = (): UtmParams => {
  const search = new URLSearchParams(window.location.search);
  const result: UtmParams = {};
  for (const key of UTM_PARAMS) {
    const value = search.get(key);
    if (value) result[key] = value;
  }
  return result;
};
